import type { AssetsTranslations } from './types'

/**
 * English translations for Assets/Fleet domain
 */
export const enTranslations: AssetsTranslations = {
  // Page/Section level
  title: "Fleet Management",
  subtitle: "Manage drivers, vehicles, and resource assignments",

  // Stats
  totalAssets: "Total Assets",
  total: "Total",
  active: "Active",
  maintenance: "In Maintenance",
  idle: "Idle",
  inMaintenance: "in maintenance",

  // Ownership types
  owned: "Owned",
  leased: "Leased",
  subcontracted: "Subcontracted",
  rented: "Rented",

  // Search and filters
  searchPlaceholder: "Search assets...",
  noAssets: "No assets found",
  noItemsFound: "No Assets Found",
  noItemsDescription: "You haven't added any assets yet. Add your first asset to get started.",
  addFirst: "Add Your First Asset",

  // Asset details
  assetDetails: "Asset Details",
  assetId: "Asset ID",
  plateNumber: "Plate Number",
  capacity: "Capacity",
  viewHistory: "View History",
  trackLocation: "Track Location",

  // Subcontracted details
  subcontractedDetails: "Subcontracted Details",
  provider: "Provider",
  contractEnd: "Contract End",
  monthlyCost: "Monthly Cost",
  contactPerson: "Contact Person",
  contactPhone: "Contact Phone",

  // Location info
  locationInfo: "Location Information",
  currentLocation: "Current Location",
  lastUsed: "Last Used",

  // Vehicle types
  vehicleType: "Vehicle Type",
  mixer: "Mixer",
  flatbed: "Flatbed",

  // Resource management
  resourceManagement: "Resource Management",
  searchResources: "Search resources...",
  createNewResource: "Create New Resource",
  createNewResourceHint: "Select or create driver & vehicle",
  noMatchingResources: "No matching resources",
  noResourcesFound: "No resources found",
  createResource: "Create Resource",
  resourceName: "Resource Name",
  resourceNamePlaceholder: "Enter resource name",
  assignedResource: "Assigned Resource",
  driverValidation: "Driver name, phone, and ID are required",
  plateValidation: "License plate must be 4 digits and 3 letters",
  driverPhoneRequiredPlaceholder: "Driver phone (required) e.g. 512345678",
  whatsappOptionalPlaceholder: "WhatsApp number (optional) e.g. 512345678",
  trailer: "Trailer",

  // Driver section
  driver: "Driver",
  searchOrCreateDriver: "Search or create driver...",
  createNewDriver: "Create New Driver",
  driverName: "Driver Name",
  phoneNumber: "Phone Number",
  driverId: "Driver ID",

  // Vehicle section
  vehicle: "Vehicle",
  searchOrCreateVehicle: "Search or create vehicle...",
  createNewVehicle: "Create New Vehicle",

  // Actions
  addAsset: "Add Asset",
  editAsset: "Edit Asset",
  deleteAsset: "Delete Asset",

  // Form section
  form: {
    name: "Asset Name",
    namePlaceholder: "Enter asset name",
    status: "Status",
    validation: {
      nameRequired: "Asset name is required",
    }
  },

  // Status
  status: {
    active: "Active",
    inactive: "Inactive",
  },

  // Toast messages
  messages: {
    createSuccess: "Asset created successfully",
    updateSuccess: "Asset updated successfully",
    deleteSuccess: "Asset deleted successfully",
    deleteConfirm: "Are you sure you want to delete this asset?",
  },

  // Common actions (for compatibility with shared components)
  common: {
    save: "Save",
    cancel: "Cancel",
    create: "Create",
    edit: "Edit",
    delete: "Delete",
    close: "Close",
    back: "Back",
    next: "Next",
    submit: "Submit",
  },

  // Table headers
  table: {
    name: "Name",
    status: "Status",
  },

  // Fleet Management Section
  fleet: {
    // Common
    title: "Fleet Management",
    subtitle: "Manage drivers, vehicles, and resource assignments",
    error: "Error",
    assetNumber: "Asset Number",
    save: "Save",
    saving: "Saving...",
    cancel: "Cancel",
    assignments: "Assignments",
    actions: "Actions",
    details: "Details",
    optional: "Optional",
    delete: "Delete",
    confirmDelete: "Are you sure you want to delete this item?",

    // Tabs
    resources: "Resources",
    drivers: "Drivers",
    vehicles: "Vehicles",
    trailers: "Trailers",
    resourcesSubtitle: "Driver and vehicle assignments",
    driversSubtitle: "Manage your drivers",
    vehiclesSubtitle: "Manage your vehicle fleet",
    trailersSubtitle: "Manage your trailers",
    search: "Search...",

    // Resources
    addResource: "Assign Resource",
    editResource: "Edit Assignment",
    resourceName: "Resource Name",
    noResources: "No resource assignments found",
    noResourcesDescription: "Get started by creating your first resource assignment.",
    assignDriver: "Assign Driver",
    assignVehicle: "Assign Vehicle",
    assignTrailer: "Assign Trailer",
    assignedSince: "Assigned Since",
    currentShipment: "Current Shipment",
    resourceStatus: "Status",
    resourceActive: "Active",
    resourceOnDelivery: "On Delivery",
    resourceReturning: "Returning",
    resourceInactive: "Inactive",
    unassign: "Unassign",
    totalResources: "Total Resources",
    activeResources: "Active Resources",
    onDelivery: "On Delivery",
    resourceDescription: "Description",
    resourceInformation: "Resource Information",
    assignment: "Assignment",
    resourceNamePlaceholderEx: "Unit Alpha",
    resourceDescriptionPlaceholder: "Primary delivery unit",
    selectDriver: "Select Driver",
    selectVehicle: "Select Vehicle",
    activateResource: "Activate",
    deactivateResource: "Deactivate",
    resourceActivated: "Resource activated",
    resourceDeactivated: "Resource deactivated",
    resourceActivatedDescription: "Resource is now active and available.",
    resourceDeactivatedDescription: "Resource has been deactivated.",
    resourceDetails: "Resource Details",
    viewResource: "View Resource",
    resourceCreated: "Resource created",
    resourceCreatedDescription: "Resource has been created successfully.",
    resourceUpdated: "Resource updated",
    resourceUpdatedDescription: "Resource has been updated successfully.",
    resourceUpdateError: "Cannot update resource: missing documentId",
    resourceDeleted: "Resource deleted",
    resourceDeletedDescription: "Resource has been soft deleted successfully.",
    resourceDeleteError: "Cannot delete resource: missing documentId",
    resourceDeleteConfirmTitle: "Delete resource?",
    resourceDeleteConfirmDescription: "This will soft delete the resource and unassign it from pending or hold shipments.",

    // Drivers
    addDriver: "Add Driver",
    editDriver: "Edit Driver",
    deleteDriver: "Delete Driver",
    noDrivers: "No drivers found",
    noDriversDescription: "Get started by adding your first driver to the fleet.",
    driverName: "Driver Name",
    driverId: "Driver ID",
    personalInformation: "Personal Information",
    driverNamePlaceholder: "Driver Name",
    driverPhoneRequiredPlaceholder: "Driver phone (required) e.g. 512345678",
    whatsappOptionalPlaceholder: "WhatsApp number (optional) e.g. 512345678",
    employeeId: "Employee ID",
    licenseNumber: "License Number",
    licenseType: "License Type",
    licenseExpiry: "License Expiry",
    selectDate: "Select date",
    licenseExpiryRequired: "License expiry is required.",
    licenseInformationHeading: "License Information",
    licenseHeavy: "Heavy Vehicle",
    licenseLight: "Light Vehicle",
    licenseHazmat: "Hazmat",
    licenseSpecial: "Special",
    license: "License",
    licenseInformation: "License Information",
    phone: "Phone",
    phoneNumber: "Phone Number",
    whatsappNumber: "WhatsApp Number",
    nationality: "Nationality",
    employeeNumber: "Employee Number",
    driverStatus: "Status",
    driverAvailable: "Available",
    driverAssigned: "Assigned",
    driverOnLeave: "On Leave",
    driverInactive: "Inactive",
    totalDrivers: "Total Drivers",
    availableDrivers: "Available",
    assignedDrivers: "Assigned",
    photo: "Photo",
    driverUpdateError: "Cannot update driver: missing documentId",
    preferredLanguage: "Preferred Language",
    preferredLanguagePlaceholder: "Select language",
    langEn: "English",
    langAr: "Arabic",
    langUr: "Urdu",

    // Vehicles
    addVehicle: "Add Vehicle",
    editVehicle: "Edit Vehicle",
    deleteVehicle: "Delete Vehicle",
    noVehicles: "No vehicles found",
    noVehiclesDescription: "Get started by adding your first vehicle to the fleet.",
    vehicleType: "Vehicle Type",
    type: "Type",
    typeTruck: "Truck",
    typeMixer: "Mixer",
    typeTanker: "Tanker",
    typeFlatbed: "Flatbed",
    typeCrane: "Crane",
    typeLoader: "Loader",
    typePump: "Pump",
    typeEquipment: "Equipment",
    make: "Make",
    model: "Model",
    makeModel: "Make / Model",
    year: "Year",
    vehicleIdentification: "Vehicle Identification",
    vehicleDetailsHeading: "Vehicle Details",
    statusAndMaintenance: "Status & Maintenance",
    makePlaceholder: "Mercedes",
    modelPlaceholder: "Actros",
    yearPlaceholder: "YYYY",
    vin: "VIN",
    plateNumber: "Plate Number",
    licensePlate: "License Plate",
    vehicleLicensePlate: "Vehicle License Plate",
    plateDigitsPlaceholder: "0000",
    plateLettersPlaceholder: "AAA",
    capacity: "Capacity",
    capacityUnit: "Unit",
    tons: "Tons",
    cubicMeters: "m³",
    vehicleStatus: "Status",
    vehicleAvailable: "Available",
    vehicleAssigned: "Assigned",
    vehicleMaintenance: "Maintenance",
    vehicleOutOfService: "Out of Service",
    ownership: "Ownership",
    owned: "Owned",
    leased: "Leased",
    rentedOut: "Rented Out",
    outsourced: "Outsourced",
    brokered: "Brokered",
    shared: "Shared",
    lastMaintenance: "Last Maintenance",
    nextMaintenance: "Next Maintenance",
    configureColumns: "Columns",
    selectVisibleColumns: "Select visible columns",
    totalVehicles: "Total Vehicles",
    availableVehicles: "Available",
    vehicleCreated: "Vehicle created",
    vehicleCreatedDescription: "Vehicle has been created successfully.",
    vehicleUpdated: "Vehicle updated",
    vehicleUpdatedDescription: "Vehicle has been updated successfully.",
    vehicleDeleted: "Vehicle deleted",
    vehicleDeletedDescription: "Vehicle has been deleted successfully.",
    vehicleUpdateError: "Cannot update vehicle: missing documentId",
    makeMercedes: "Mercedes",
    makeVolvo: "Volvo",
    makeScania: "Scania",
    makeMAN: "MAN",
    makeDAF: "DAF",
    makeIveco: "Iveco",
    makeHino: "Hino",
    makeIsuzu: "Isuzu",
    makeMitsubishi: "Mitsubishi",
    makeRenault: "Renault",
    makeOther: "Other",

    // Trailers
    trailer: "Trailer",
    addTrailer: "Add Trailer",
    editTrailer: "Edit Trailer",
    deleteTrailer: "Delete Trailer",
    noTrailers: "No trailers found",
    noTrailersDescription: "Get started by adding your first trailer to the fleet.",
    trailerStatus: "Trailer Status",
    trailerAvailable: "Available",
    trailerAssigned: "Assigned",
    trailerMaintenance: "Maintenance",
    trailerOutOfService: "Out of Service",
    selectStatus: "Select Status",

    // Sort options - Drivers
    sortByName: "Name",
    sortNameAsc: "Name (A-Z)",
    sortNameDesc: "Name (Z-A)",
    sortByLicenseExpiry: "License Expiry",
    sortLicenseExpiryAsc: "License Expiry (Earliest First)",
    sortLicenseExpiryDesc: "License Expiry (Latest First)",
    sortByStatus: "Status",
    sortStatusAsc: "Status (A-Z)",
    sortStatusDesc: "Status (Z-A)",
    sortByDateAdded: "Date Added",
    sortOldest: "Oldest First",
    sortNewest: "Newest First",

    // Sort options - Vehicles
    sortByPlateNumber: "Plate Number",
    sortPlateAsc: "Plate Number (A-Z)",
    sortPlateDesc: "Plate Number (Z-A)",
    sortByMake: "Make",
    sortMakeAsc: "Make (A-Z)",
    sortMakeDesc: "Make (Z-A)",
    sortByCapacity: "Capacity",
    sortCapacityAsc: "Capacity (Low to High)",
    sortCapacityDesc: "Capacity (High to Low)",
    sortByOwnership: "Ownership",
    sortOwnershipAsc: "Ownership (A-Z)",
    sortOwnershipDesc: "Ownership (Z-A)",

    // Sort options - Resources
    sortByAssignedSince: "Assigned Since",
    sortAssignedOldest: "Assigned Earliest First",
    sortAssignedNewest: "Assigned Latest First",

    // Filter Labels
    filterDriverName: "Driver Name",
    filterDriverPhone: "Phone Number",
    filterEmployeeNumber: "Employee Number",
    filterLicensePlate: "License Plate",
    filterBindingStatus: "Binding Status",
    filterBindingStatusAll: "All",
    filterBindingStatusYes: "Assigned",
    filterBindingStatusNo: "Unassigned",
    filterDriverNamePlaceholder: "Search by driver name...",
    filterPhoneNumberPlaceholder: "Search by phone...",
    filterEmployeeNumberPlaceholder: "Search by employee number...",
    filterLicensePlatePlaceholder: "Search by license plate...",
    filterVehicleLicensePlate: "Vehicle License Plate",
    searchByName: "Search by name",
    searchByPhone: "Search by phone",
    searchByEmployeeNumber: "Search by employee number",
    searchByLicensePlate: "Search by license plate",
    searchByDriverName: "Search by driver name",
    searchByVehiclePlate: "Search by vehicle plate",
    searchByDriverPhone: "Search by driver phone",
    enterDriverName: "Enter driver name",
    enterPhoneNumber: "Enter phone number",
    enterEmployeeNumber: "Enter employee number",
    enterLicensePlate: "Enter license plate",
    bindingStatus: "Binding Status",
    bindedToResource: "Bound to resource",
    notBinded: "Not bound",
    driverPhone: "Driver Phone",

    // Detail view sections
    basicInformation: "Basic Information",
    specifications: "Specifications",
    additionalInfo: "Additional Information",
    description: "Description",

    // Deactivation warning banner
    deactivationWarning: {
      title: "Entity deactivated",
      shipmentCount: "{{count}} order(s) were automatically unassigned and need reassignment",
      shipmentList: "Affected orders: {{list}}",
      resourceCount: "{{count}} resource(s) also set to inactive",
      resourceList: "Affected resources: {{list}}",
      viewOrders: "See Affected Orders",
      dismiss: "Dismiss",
    },

    // Toast messages for fleet UI actions
    exportingTitle: "Exporting...",
    exportingDesc: "Fetching all records",
    exportFailedTitle: "Export failed",
    exportDriversFailed: "Could not export drivers",
    exportVehiclesFailed: "Could not export vehicles",
    exportResourcesFailed: "Could not export resources",
    createDriverFailed: "Failed to create driver",
    createVehicleFailed: "Failed to create vehicle",
    createResourceFailed: "Failed to create resource",
    importingTitle: "Importing...",
    importingDesc: "Processing file records",
    importSuccess: "Import complete",
    importSuccessDesc: "{{created}} created, {{skipped}} skipped",
    importFailed: "Import failed",
    importDriversFailed: "Could not import drivers",
    importVehiclesFailed: "Could not import vehicles",
    importResourcesFailed: "Could not import resources",
    importNoRows: "No valid rows found in file",
    importSelectTenantFirst: "Select a tenant first",
    importSelectTenantFirstDescription: "Please select a specific tenant from the top bar before importing data.",
    importDriversLabel: "Upload Drivers",
    importVehiclesLabel: "Upload Vehicles",
    importResourcesLabel: "Upload Resources",
  },

  // Countries
  countries: {
    saudiArabia: "Saudi Arabia",
    uae: "United Arab Emirates",
    egypt: "Egypt",
    pakistan: "Pakistan",
    india: "India",
    bangladesh: "Bangladesh",
    philippines: "Philippines",
    nepal: "Nepal",
    jordan: "Jordan",
    syria: "Syria",
    yemen: "Yemen",
    sudan: "Sudan",
    other: "Other",
  },

  // Top-level convenience keys (flat versions for direct access)
  name: "Name",
  namePlaceholder: "Enter asset name",
  statusLabel: "Status",
  nameRequired: "Asset name is required",
  inactive: "Inactive",
  createSuccess: "Asset created successfully",
  updateSuccess: "Asset updated successfully",
  deleteSuccess: "Asset deleted successfully",
  deleteConfirm: "Are you sure you want to delete this asset?",
  actions: "Actions",
  details: "Details",
  optional: "Optional",
  delete: "Delete",
  confirmDelete: "Are you sure you want to delete this item?",
  resources: "Resources",
  drivers: "Drivers",
  vehicles: "Vehicles",
  trailers: "Trailers",
  resourcesSubtitle: "Driver and vehicle assignments",
  driversSubtitle: "Manage your drivers",
  vehiclesSubtitle: "Manage your vehicle fleet",
  trailersSubtitle: "Manage your trailers",
  search: "Search...",
  addResource: "Assign Resource",
  editResource: "Edit Assignment",
  noResources: "No resource assignments found",
  noResourcesDescription: "Get started by creating your first resource assignment.",
  assignDriver: "Assign Driver",
  assignVehicle: "Assign Vehicle",
  assignTrailer: "Assign Trailer",
  assignedSince: "Assigned Since",
  currentShipment: "Current Shipment",
  resourceStatus: "Status",
  resourceActive: "Active",
  resourceOnDelivery: "On Delivery",
  resourceReturning: "Returning",
  resourceInactive: "Inactive",
  unassign: "Unassign",
  totalResources: "Total Resources",
  activeResources: "Active Resources",
  onDelivery: "On Delivery",
  resourceDescription: "Description",
  activateResource: "Activate",
  deactivateResource: "Deactivate",
  resourceActivated: "Resource activated",
  resourceDeactivated: "Resource deactivated",
  resourceActivatedDescription: "Resource is now active and available.",
  resourceDeactivatedDescription: "Resource has been deactivated.",
  resourceDetails: "Resource Details",
  viewResource: "View Resource",
  addDriver: "Add Driver",
  editDriver: "Edit Driver",
  deleteDriver: "Delete Driver",
  noDrivers: "No drivers found",
  noDriversDescription: "Get started by adding your first driver to the fleet.",
  driverNameAr: "Driver Name (Arabic)",
  employeeId: "Employee ID",
  licenseNumber: "License Number",
  licenseType: "License Type",
  licenseExpiry: "License Expiry",
  selectDate: "Select date",
  licenseExpiryRequired: "License expiry is required.",
  licenseHeavy: "Heavy Vehicle",
  licenseLight: "Light Vehicle",
  licenseHazmat: "Hazmat",
  licenseSpecial: "Special",
  phone: "Phone",
  whatsappNumber: "WhatsApp Number",
  driverStatus: "Status",
  driverAvailable: "Available",
  driverAssigned: "Assigned",
  driverOnLeave: "On Leave",
  driverInactive: "Inactive",
  totalDrivers: "Total Drivers",
  availableDrivers: "Available",
  assignedDrivers: "Assigned",
  photo: "Photo",
  addVehicle: "Add Vehicle",
  editVehicle: "Edit Vehicle",
  deleteVehicle: "Delete Vehicle",
  noVehicles: "No vehicles found",
  noVehiclesDescription: "Get started by adding your first vehicle to the fleet.",
  typeTruck: "Truck",
  typeMixer: "Mixer",
  typeTanker: "Tanker",
  typeFlatbed: "Flatbed",
  typeCrane: "Crane",
  typeLoader: "Loader",
  typePump: "Pump",
  typeEquipment: "Equipment",
  make: "Make",
  model: "Model",
  makeModel: "Make / Model",
  year: "Year",
  vin: "VIN",
  capacityUnit: "Unit",
  tons: "Tons",
  cubicMeters: "m³",
  vehicleStatus: "Status",
  vehicleAvailable: "Available",
  vehicleAssigned: "Assigned",
  vehicleMaintenance: "Maintenance",
  vehicleOutOfService: "Out of Service",
  ownership: "Ownership",
  rentedOut: "Rented Out",
  outsourced: "Outsourced",
  brokered: "Brokered",
  shared: "Shared",
  lastMaintenance: "Last Maintenance",
  nextMaintenance: "Next Maintenance",
  configureColumns: "Columns",
  selectVisibleColumns: "Select visible columns",
  totalVehicles: "Total Vehicles",
  availableVehicles: "Available",
  addTrailer: "Add Trailer",
  editTrailer: "Edit Trailer",
  deleteTrailer: "Delete Trailer",
  noTrailers: "No trailers found",
  noTrailersDescription: "Get started by adding your first trailer to the fleet.",
  sortByName: "Name",
  sortNameAsc: "Name (A-Z)",
  sortNameDesc: "Name (Z-A)",
  sortByLicenseExpiry: "License Expiry",
  sortLicenseExpiryAsc: "License Expiry (Earliest First)",
  sortLicenseExpiryDesc: "License Expiry (Latest First)",
  sortByStatus: "Status",
  sortStatusAsc: "Status (A-Z)",
  sortStatusDesc: "Status (Z-A)",
  sortByDateAdded: "Date Added",
  sortOldest: "Oldest First",
  sortNewest: "Newest First",
  sortByPlateNumber: "Plate Number",
  sortPlateAsc: "Plate Number (A-Z)",
  sortPlateDesc: "Plate Number (Z-A)",
  sortByMake: "Make",
  sortMakeAsc: "Make (A-Z)",
  sortMakeDesc: "Make (Z-A)",
  sortByCapacity: "Capacity",
  sortCapacityAsc: "Capacity (Low to High)",
  sortCapacityDesc: "Capacity (High to Low)",
  sortByOwnership: "Ownership",
  sortOwnershipAsc: "Ownership (A-Z)",
  sortOwnershipDesc: "Ownership (Z-A)",
  sortByAssignedSince: "Assigned Since",
  sortAssignedOldest: "Assigned Earliest First",
  sortAssignedNewest: "Assigned Latest First",
  filterDriverName: "Driver Name",
  filterDriverPhone: "Phone Number",
  filterEmployeeNumber: "Employee Number",
  filterLicensePlate: "License Plate",
  filterBindingStatus: "Binding Status",
  filterBindingStatusAll: "All",
  filterBindingStatusYes: "Assigned",
  filterBindingStatusNo: "Unassigned",
  filterDriverNamePlaceholder: "Search by driver name...",
  filterPhoneNumberPlaceholder: "Search by phone...",
  filterEmployeeNumberPlaceholder: "Search by employee number...",
  filterLicensePlatePlaceholder: "Search by license plate...",
  filterVehicleLicensePlate: "Vehicle License Plate",
  saudiArabia: "Saudi Arabia",
  uae: "United Arab Emirates",
  egypt: "Egypt",
  pakistan: "Pakistan",
  india: "India",
  bangladesh: "Bangladesh",
  philippines: "Philippines",
  nepal: "Nepal",
  jordan: "Jordan",
  syria: "Syria",
  yemen: "Yemen",
  sudan: "Sudan",
  other: "Other",

  // Section headings
  vehicleIdentification: "Vehicle Identification",
  vehicleDetailsHeading: "Vehicle Details",
  statusAndMaintenance: "Status & Maintenance",
  personalInformation: "Personal Information",
  licenseInformationHeading: "License Information",
  trailerInformation: "Trailer Information",
  resourceInformation: "Resource Information",
  assignment: "Assignment",

  // Placeholders
  makePlaceholder: "Mercedes",
  modelPlaceholder: "Actros",
  yearPlaceholder: "YYYY",
  driverNamePlaceholder: "Name",
  licensePlaceholder: "DL-12345",
  trailerMakePlaceholder: "Enter make",
  trailerModelPlaceholder: "Enter model",
  vinPlaceholder: "Enter VIN",
  descriptionPlaceholder: "Enter description",
  resourceNamePlaceholderEx: "Unit Alpha",
  resourceDescriptionPlaceholder: "Primary delivery unit",
  plateDigitsPlaceholder: "0000",
  plateLettersPlaceholder: "AAA",
  trailerIdPlaceholder: "TRL-001",
  trailerPlatePlaceholder: "ABC-123",
  trailerYearPlaceholder: "2024",
  licensePlateExamplePlaceholder: "e.g. 5080-ZZR",

  // Select options
  selectDriver: "Select Driver",
  selectVehicle: "Select Vehicle",

  errors: {
    driverNotFound: 'Driver not found',
    vehicleNotFound: 'Vehicle not found',
    trailerNotFound: 'Trailer not found',
    resourceNotFound: 'Resource not found',
  },
}
